import React, { useEffect, useState } from 'react';
import { ArrowLeft, MessageCircle, Settings, Mail, Send, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const SupportTickets = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('mine'); // mine | all | settings

  // Admin notification settings
  const [settings, setSettings] = useState({
    notification_email: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Snagcart Support',
    smtp_use_tls: true,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showAdvancedSmtp, setShowAdvancedSmtp] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const isAdmin = Boolean(user?.is_platform_admin);

  const loadUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setUser(data);
    } catch (e) { /* ignore */ }
  };

  const loadTickets = async (mode = 'mine') => {
    try {
      const token = localStorage.getItem('token');
      const url =
        mode === 'all'
          ? `${backendUrl}/api/support/admin/tickets`
          : `${backendUrl}/api/support/tickets`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTickets(data);
    } catch (e) { /* ignore */ }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/support/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSettings(prev => ({ ...prev, ...data, smtp_password: '' }));
      if (data.smtp_host) setShowAdvancedSmtp(true);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadUser();
    loadTickets('mine');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin && activeTab === 'settings') {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, activeTab]);

  const updateStatus = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/support/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to update status');
      }
      toast.success('Status updated');
      loadTickets('all');
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in both Subject and Details.');
      return;
    }
    if (subject.trim().length < 3) {
      toast.error('Subject must be at least 3 characters.');
      return;
    }
    if (message.trim().length < 5) {
      toast.error('Details must be at least 5 characters.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('You must be logged in to submit a ticket.');
      navigate('/auth');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subject: subject.trim(), message: message.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (Array.isArray(data.detail)) {
          const msgs = data.detail.map(e => e.msg).join('; ');
          throw new Error(msgs);
        }
        throw new Error(data.detail || 'Failed to create ticket');
      }
      toast.success('Ticket created successfully!');
      setSubject('');
      setMessage('');
      loadTickets(activeTab === 'all' && isAdmin ? 'all' : 'mine');
    } catch (err) {
      toast.error(err.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!settings.notification_email.trim()) {
      toast.error('Please enter a notification email address.');
      return;
    }
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/support/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to save settings');
      toast.success('Notification settings saved!');
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/support/admin/test-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Test email failed');
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      toast.error(err.message || 'Test email failed');
    } finally {
      setTestLoading(false);
    }
  };

  const statusColor = (s) => {
    if (s === 'open') return 'text-amber-400 border-amber-400/30';
    if (s === 'in_progress') return 'text-blue-400 border-blue-400/30';
    if (s === 'resolved') return 'text-green-400 border-green-400/30';
    return 'text-zinc-400 border-zinc-700';
  };

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-violet-400" />
              Report issue
            </h1>
            <p className="text-zinc-400 text-sm">
              Submit a support ticket and our team will respond promptly.
            </p>
            <p className="mt-2 text-sm text-zinc-300" data-testid="support-email-help">
              Need help setting up the platform?{' '}
              <a
                href="mailto:support@snagcart.io?subject=Snagcart%20setup%20help"
                className="font-semibold text-violet-300 hover:text-violet-200 underline underline-offset-2"
              >
                support@snagcart.io
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,1.2fr] gap-8">
        {/* Left: New ticket form */}
        <div className="glass p-6 rounded-2xl">
          <h2 className="text-base font-heading font-semibold mb-4">Report issue</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="subject" className="text-xs mb-1 block">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Describe your issue in a few words"
                className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-11 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="message" className="text-xs mb-1 block">Details</Label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share as much detail as possible so we can help quickly."
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-violet-500 rounded-xl text-sm px-3 py-2 min-h-[120px] text-white placeholder-zinc-500 outline-none"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !subject || !message}
              className="rounded-full px-6"
            >
              {loading ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </form>
        </div>

        {/* Right: Tickets list + Admin tabs */}
        <div className="glass p-6 rounded-2xl">
          {/* Tab bar */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-heading font-semibold">
              {activeTab === 'settings' ? 'Notification Settings' : 'Tickets'}
            </h2>
            {isAdmin && (
              <div className="inline-flex rounded-full bg-black/40 p-1 text-[11px] gap-0.5">
                <button
                  onClick={() => { setActiveTab('mine'); loadTickets('mine'); }}
                  className={`px-3 py-1 rounded-full transition-colors ${activeTab === 'mine' ? 'bg-violet-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
                >
                  My Tickets
                </button>
                <button
                  onClick={() => { setActiveTab('all'); loadTickets('all'); }}
                  className={`px-3 py-1 rounded-full transition-colors ${activeTab === 'all' ? 'bg-violet-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
                >
                  All Tickets
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-3 py-1 rounded-full flex items-center gap-1 transition-colors ${activeTab === 'settings' ? 'bg-violet-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
                >
                  <Settings className="w-3 h-3" /> Settings
                </button>
              </div>
            )}
          </div>

          {/* Settings panel (admin only) */}
          {activeTab === 'settings' && isAdmin && (
            <form onSubmit={handleSaveSettings} className="space-y-5">
              {/* Notification email */}
              <div className="border border-violet-500/20 rounded-xl p-4 bg-violet-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-300">Notification Email</span>
                </div>
                <p className="text-[11px] text-zinc-400 mb-3">
                  Every new support ticket will be sent to this address. Leave SMTP fields empty to use the server's default email provider.
                </p>
                <div>
                  <Label className="text-xs mb-1 block">Receive tickets at</Label>
                  <Input
                    type="email"
                    value={settings.notification_email}
                    onChange={(e) => setSettings(s => ({ ...s, notification_email: e.target.value }))}
                    placeholder="you@yourcompany.com"
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                  />
                </div>
              </div>

              {/* Advanced SMTP toggle */}
              <button
                type="button"
                onClick={() => setShowAdvancedSmtp(v => !v)}
                className="text-[11px] text-zinc-400 hover:text-violet-400 flex items-center gap-1 transition-colors"
              >
                <Settings className="w-3 h-3" />
                {showAdvancedSmtp ? 'Hide' : 'Configure'} custom SMTP server (optional)
              </button>

              {showAdvancedSmtp && (
                <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-black/30">
                  <p className="text-[11px] text-zinc-500">
                    Optional: use your own SMTP server to send notifications. If left blank, the platform default is used.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block">SMTP Host</Label>
                      <Input
                        value={settings.smtp_host}
                        onChange={(e) => setSettings(s => ({ ...s, smtp_host: e.target.value }))}
                        placeholder="smtp.gmail.com"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Port</Label>
                      <Input
                        type="number"
                        value={settings.smtp_port}
                        onChange={(e) => setSettings(s => ({ ...s, smtp_port: parseInt(e.target.value) || 587 }))}
                        placeholder="587"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Username / Email</Label>
                    <Input
                      value={settings.smtp_username}
                      onChange={(e) => setSettings(s => ({ ...s, smtp_username: e.target.value }))}
                      placeholder="you@gmail.com"
                      className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-9 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Password / App Password</Label>
                    <div className="relative">
                      <Input
                        type={showSmtpPassword ? 'text' : 'password'}
                        value={settings.smtp_password}
                        onChange={(e) => setSettings(s => ({ ...s, smtp_password: e.target.value }))}
                        placeholder="Leave blank to keep existing"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-9 text-xs pr-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmtpPassword(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showSmtpPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block">From Email</Label>
                      <Input
                        value={settings.smtp_from_email}
                        onChange={(e) => setSettings(s => ({ ...s, smtp_from_email: e.target.value }))}
                        placeholder="support@snagcart.io"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">From Name</Label>
                      <Input
                        value={settings.smtp_from_name}
                        onChange={(e) => setSettings(s => ({ ...s, smtp_from_name: e.target.value }))}
                        placeholder="Snagcart Support"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-9 text-xs"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.smtp_use_tls}
                      onChange={(e) => setSettings(s => ({ ...s, smtp_use_tls: e.target.checked }))}
                      className="rounded"
                    />
                    Use STARTTLS (recommended for port 587)
                  </label>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-3 pt-1">
                <Button
                  type="submit"
                  disabled={settingsLoading}
                  className="rounded-full px-5 text-sm flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  {settingsLoading ? 'Saving...' : 'Save Settings'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={testLoading || !settings.notification_email}
                  onClick={handleTestEmail}
                  className="rounded-full px-5 text-sm flex items-center gap-2 border-zinc-700 text-zinc-300 hover:border-violet-500 hover:text-violet-300"
                >
                  <Send className="w-3.5 h-3.5" />
                  {testLoading ? 'Sending...' : 'Send Test Email'}
                </Button>
              </div>
              {settings.notification_email && (
                <p className="text-[11px] text-zinc-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Notifications will be sent to <span className="text-violet-400">{settings.notification_email}</span>
                </p>
              )}
            </form>
          )}

          {/* Ticket list */}
          {activeTab !== 'settings' && (
            <>
              {tickets.length === 0 ? (
                <p className="text-xs text-zinc-500">
                  {activeTab === 'all' ? 'No tickets found yet.' : "You haven't raised any tickets yet."}
                </p>
              ) : (
                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1 text-xs">
                  {tickets.map((t) => (
                    <div
                      key={t.id}
                      className="border border-zinc-800 rounded-xl px-4 py-3 bg-black/40 flex flex-col gap-1"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold text-zinc-100">{t.subject}</span>
                          {activeTab === 'all' && (
                            <span className="text-[10px] text-zinc-500">
                              From: {t.user_name || t.user_email || t.user_id}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide ${statusColor(t.status)}`}>
                            {t.status?.replace('_', ' ')}
                          </span>
                          {isAdmin && activeTab === 'all' && (
                            <select
                              value={t.status}
                              onChange={(e) => updateStatus(t.id, e.target.value)}
                              className="bg-black/60 border border-zinc-800 text-[10px] rounded-full px-2 py-1 text-zinc-300"
                            >
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          )}
                        </div>
                      </div>
                      <p className="text-zinc-400 leading-snug text-[11px] whitespace-pre-line mt-1">{t.message}</p>
                      <span className="text-[10px] text-zinc-500 mt-1">
                        {t.created_at ? new Date(t.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTickets;
